import { BaseConfirmAlertProps } from '@/components/ConfirmAlert/ConfirmAlert';
import { useEffect } from 'react';

export type EventType = {
	[key: string]: (...args: any[]) => void;
};

export type LocationResult =
	| {
			type: 'SUCCESS';
			data: {
				lat: number;
				lng: number;
			};
	  }
	| {
			type: 'ERROR';
			message: string;
	  };

export type ManualLocationRequest = {
	expiresAt: number;
};

type VadioEvents = {
	SHOW_CONFIRM_ALERT: (props: BaseConfirmAlertProps) => void;
	MANUAL_LOCATION_INPUT: (request: ManualLocationRequest) => void;
	MANUAL_LOCATION_INPUT_RESULT: (result: LocationResult) => void;
};

class EventBus<T extends EventType> {
	private listeners = new Map<keyof T, T[keyof T][]>();

	on<U extends keyof T>(event: U, listener: T[U]) {
		const existingKey = this.listeners.get(event);

		if (existingKey) {
			existingKey.push(listener);
		} else {
			this.listeners.set(event, [listener]);
		}

		return () => {
			const listeners = this.listeners.get(event);
			if (listeners) {
				const index = listeners.indexOf(listener);
				if (index > -1) {
					listeners.splice(index, 1);
				}
			}
		};
	}

	emit<U extends keyof T>(event: U, ...args: Parameters<T[U]>): void {
		const listeners = this.listeners.get(event);
		if (!listeners) return;
		// A copy, because a listener may unsubscribe itself mid-emit and shift the ones after it.
		[...listeners].forEach((listener) => listener(...args));
	}
}

export const eventsService = new EventBus<VadioEvents>();

// for debugging purposes
if (import.meta.env.MODE === 'development') {
	(window as any).tixioEventEmitter = eventsService;
}

export const useEvent = <T extends keyof VadioEvents>(event: T, handler: VadioEvents[T]) => {
	useEffect(() => {
		return eventsService.on(event, handler);
	}, [event, handler]);
};
